import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FileText, ImageIcon, Music, Video, FileQuestion, Loader2,
  ShieldAlert, EyeOff, ChevronDown, ChevronUp, Lock, AlertTriangle, RotateCw,
  MessageSquare, MapPinned, ListChecks, Camera, Mic,
} from "lucide-react";
import { agencyGetCaseFile, fetchAgencyAttachmentBlob, agencyListAmendments } from "../lib/api";
import { MedicalProfileView } from "./MedicalProfileView";

/**
 * Iter56 (Phase 3) / Iter66 (Phase 1) — Agency case-file panel with
 * cross-origin-safe evidence loading.
 *
 * Drops into any agency alert card. Lazy-loads the scoped case file
 * via ``GET /api/agency/alerts/{id}/case-file`` only when the
 * dispatcher expands the panel — keeps the dashboard list fast and
 * avoids leaking attachment URLs into a giant batched payload.
 *
 * Iter66 — Each attachment is fetched as a `Blob` with
 * `credentials: "include"` then rendered through `URL.createObjectURL`.
 * Required for production where `api.sosmali.com` is a different
 * origin from `admin.sosmali.com` and the raw <img src> request would
 * be sent without the HttpOnly agency cookie → 401. The blob pattern
 * always carries credentials, no matter the origin.
 *
 * Permissions are still server-enforced: a 401/403 from the blob
 * fetch means the back-end decided the caller isn't the assigned
 * agency anymore. We surface a clear French message ("Accès refusé").
 */
const KIND_ICON = {
  image: ImageIcon,
  audio: Music,
  video: Video,
  file:  FileText,
};

// Iter66 — French label + icon for each citizen amendment change type.
const AMEND_META = {
  evidence_added:                { icon: Camera,        label: "Preuve ajoutée" },
  photo_added:                   { icon: ImageIcon,     label: "Photo ajoutée" },
  audio_added:                   { icon: Mic,           label: "Audio ajouté" },
  video_added:                   { icon: Video,         label: "Vidéo ajoutée" },
  description_added:             { icon: MessageSquare, label: "Information ajoutée" },
  description_corrected:         { icon: MessageSquare, label: "Description corrigée" },
  citizen_note_added:            { icon: MessageSquare, label: "Note ajoutée" },
  location_clarification_added:  { icon: MapPinned,     label: "Lieu précisé" },
  category_correction_requested: { icon: ListChecks,    label: "Catégorie à corriger" },
};

const fmtBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} ko`;
  return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

/**
 * Single attachment tile. Owns its own blob URL lifecycle (created on
 * mount, revoked on unmount) so we don't leak object URLs when the
 * dispatcher closes the case file or navigates away.
 */
function AttachmentTile({ alertId, attachment, idx }) {
  const Icon = KIND_ICON[attachment.kind] || FileQuestion;
  const [state, setState] = useState({ status: "loading", url: null, error: null });
  const urlRef = useRef(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", url: null, error: null });

    fetchAgencyAttachmentBlob(attachment.scoped_url)
      .then((res) => {
        if (cancelled) {
          URL.revokeObjectURL(res.url);
          return;
        }
        urlRef.current = res.url;
        setState({ status: "ok", url: res.url, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          status: "err",
          url: null,
          error: e?.message || "Impossible de charger la pièce jointe",
        });
      });

    return () => {
      cancelled = true;
      // Defer revoke to the next tick so an in-flight <img onLoad>
      // doesn't fight with the revoke. The browser holds a reference
      // anyway until the element is detached.
      const u = urlRef.current;
      if (u) {
        setTimeout(() => { try { URL.revokeObjectURL(u); } catch (_e) { /* noop */ } }, 0);
        urlRef.current = null;
      }
    };
  }, [attachment.scoped_url, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  return (
    <div
      className="border-2 border-rescue-line bg-white p-2 space-y-1"
      data-testid={`agency-attachment-${alertId}-${idx}`}
    >
      <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
        <Icon className="w-3 h-3" /> {attachment.kind} · {fmtBytes(attachment.size)}
      </div>

      {state.status === "loading" && (
        <div
          data-testid={`agency-attachment-loading-${alertId}-${idx}`}
          className="w-full h-32 border-2 border-dashed border-rescue-line bg-rescue-bg/40 flex items-center justify-center text-[10px] font-bold uppercase tracking-[0.18em] text-rescue-mute"
        >
          <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Téléversement en cours
        </div>
      )}

      {state.status === "err" && (
        <div
          data-testid={`agency-attachment-error-${alertId}-${idx}`}
          className="w-full h-32 border-2 border-rose-400 bg-rose-50 flex flex-col items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-rose-700 px-2 text-center"
        >
          <AlertTriangle className="w-4 h-4" />
          <span>{state.error}</span>
          <button
            type="button"
            onClick={retry}
            data-testid={`agency-attachment-retry-${alertId}-${idx}`}
            className="mt-1 inline-flex items-center gap-1 px-2 py-1 border border-rose-600 text-rose-700 hover:bg-rose-100"
          >
            <RotateCw className="w-3 h-3" /> Réessayer
          </button>
        </div>
      )}

      {state.status === "ok" && (
        <>
          {attachment.kind === "image" ? (
            <img
              src={state.url}
              alt={attachment.name || `pièce ${idx + 1}`}
              className="w-full h-32 object-cover border-2 border-rescue-line"
              loading="lazy"
            />
          ) : attachment.kind === "audio" ? (
            <audio controls src={state.url} className="w-full h-8" />
          ) : attachment.kind === "video" ? (
            <video controls src={state.url} className="w-full h-32 object-cover border-2 border-rescue-line" />
          ) : (
            <a
              href={state.url}
              download={attachment.name || `piece-${idx + 1}`}
              className="text-[11px] underline text-rescue-ink hover:text-emerald-700 break-all"
            >
              {attachment.name || `pièce ${idx + 1}`}
            </a>
          )}
        </>
      )}
    </div>
  );
}

export function AgencyCaseFilePanel({ alertId }) {
  const [open, setOpen] = useState(false);
  const [cf, setCf] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [amendments, setAmendments] = useState([]);
  // Guards against react-StrictMode + same-dep-loop: only kick off one
  // fetch per (alertId, open=true) cycle. Previously this lived in
  // `useEffect` deps which made setBusy(true) re-trigger the effect,
  // tear down its own cleanup, and leave `busy` permanently true
  // because the in-flight Promise was always marked cancelled before
  // its .finally could fire.
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      // Reset on collapse so a re-open does a fresh fetch (covers
      // ‘reassignment changed something while the panel was closed’).
      fetchedRef.current = false;
      return undefined;
    }
    if (fetchedRef.current) return undefined;
    fetchedRef.current = true;
    let cancelled = false;
    setBusy(true);
    setErr(null);
    Promise.all([
      agencyGetCaseFile(alertId),
      agencyListAmendments(alertId).catch(() => ({ items: [] })),
    ])
      .then(([data, am]) => {
        if (cancelled) return;
        setCf(data);
        setAmendments(am?.items || []);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || "Accès refusé";
        setErr(msg);
        // Allow a future re-open to retry.
        fetchedRef.current = false;
      })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [open, alertId]);

  return (
    <div
      className="mt-3 border-2 border-rescue-line bg-white"
      data-testid={`agency-case-file-${alertId}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`agency-case-file-toggle-${alertId}`}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-rescue-line/40 transition-colors"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-rescue-ink">
          <FileText className="w-3.5 h-3.5" /> Dossier de l&apos;alerte
          <span className="text-rescue-mute font-normal normal-case">— preuves &amp; contexte</span>
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-rescue-mute" /> : <ChevronDown className="w-3.5 h-3.5 text-rescue-mute" />}
      </button>
      {open && (
        <div className="border-t-2 border-rescue-line p-3 space-y-3 bg-rescue-line/20">
          {busy && (
            <div className="flex items-center gap-2 text-[11px] text-rescue-mute">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement du dossier…
            </div>
          )}
          {err && (
            <div className="text-[11px] text-rose-700 border-l-2 border-rose-600 pl-2">
              {err}
            </div>
          )}
          {cf && (
            <>
              {/* Sensitivity watermark */}
              {cf.sensitive && (
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700 border-2 border-rose-600 bg-rose-50 px-2 py-1">
                  <ShieldAlert className="w-3.5 h-3.5" /> Cas sensible — manipulation confidentielle requise
                </div>
              )}

              {/* Read-only badge */}
              <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-rescue-mute">
                <Lock className="w-3 h-3" />
                <span>Lecture seule</span>
                <span className="ml-auto text-rescue-mute/70">{(cf.attachments || []).length} pièce(s) jointe(s)</span>
              </div>

              {/* Verification note + dispatch context */}
              {(cf.verification_note || cf.central_review_status) && (
                <div className="text-[11px] space-y-1">
                  <div>
                    <span className="font-bold text-rescue-ink">Vérification :</span>{" "}
                    <span className="text-rescue-mute">{cf.central_review_status || "—"}</span>
                  </div>
                  {cf.verification_note && (
                    <div className="italic text-rescue-mute pl-2 border-l-2 border-rescue-line">
                      « {cf.verification_note} »
                    </div>
                  )}
                </div>
              )}

              {/* Reporter description */}
              {cf.description && (
                <div className="text-[11px]">
                  <span className="font-bold text-rescue-ink">Description du citoyen :</span>
                  <div className="text-rescue-mute mt-0.5">{cf.description}</div>
                </div>
              )}

              {/* Iter74 — Emergency medical profile snapshot. Only rendered
                  once the agency is assigned (backend filters unassigned
                  agencies at `_check_agency_assigned`), so this component
                  is safe to always render — it self-hides when the field
                  is null. */}
              <MedicalProfileView
                profile={cf.medical_profile_snapshot}
                alertId={alertId}
                emergencyType={cf.emergency_type}
                actor="agency"
                testid={`agency-case-file-medical-${alertId}`}
              />

              {/* Attachments grid */}
              {(cf.attachments || []).length > 0 ? (
                <div className="grid grid-cols-2 gap-2" data-testid={`agency-case-file-attachments-${alertId}`}>
                  {cf.attachments.map((a, idx) => (
                    <AttachmentTile key={`${a.scoped_url}-${idx}`} alertId={alertId} attachment={a} idx={idx} />
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-[10px] text-rescue-mute italic">
                  <EyeOff className="w-3 h-3" /> Aucune preuve attachée par le citoyen.
                </div>
              )}

              {/* Iter66 — Citizen amendments timeline */}
              {amendments.length > 0 && (
                <div className="border-2 border-cyan-300 bg-cyan-50 p-2" data-testid={`agency-amendments-${alertId}`}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-800 flex items-center gap-1.5 mb-2">
                    <MessageSquare className="w-3 h-3" /> Ajouté par le citoyen ({amendments.length})
                  </div>
                  <ol className="space-y-2">
                    {amendments.map((a, i) => {
                      const meta = AMEND_META[a.change_type] || AMEND_META.citizen_note_added;
                      const Icon = meta.icon;
                      return (
                        <li
                          key={a.id || i}
                          className="border-l-2 border-cyan-500 pl-2 py-0.5"
                          data-testid={`agency-amendment-${alertId}-${i}`}
                        >
                          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-800 inline-flex items-center gap-1">
                            <Icon className="w-3 h-3" /> {meta.label}
                          </div>
                          {a.note && <div className="text-[11px] text-rescue-ink mt-0.5">{a.note}</div>}
                          {a.location_clarification && (
                            <div className="text-[11px] text-rescue-ink mt-0.5"><MapPinned className="w-3 h-3 inline mr-1" />{a.location_clarification}</div>
                          )}
                          {a.requested_category && (
                            <div className="text-[11px] text-rescue-ink mt-0.5">→ {a.requested_category}</div>
                          )}
                          {(a.attachments || []).length > 0 && (
                            <div className="text-[10px] text-rescue-mute mt-0.5 italic">
                              {a.attachments.length} pièce(s) jointe(s) — voir dossier ci-dessus
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}

              {/* Reassignment history (spec §8: new agency must see prior) */}
              {(cf.reassignment_history || []).length > 0 && (
                <details className="text-[11px]" data-testid={`agency-reassign-history-${alertId}`}>
                  <summary className="cursor-pointer text-rescue-ink font-bold uppercase tracking-[0.15em] text-[10px]">
                    Historique des réaffectations ({cf.reassignment_history.length})
                  </summary>
                  <ul className="mt-1 space-y-1 pl-2">
                    {cf.reassignment_history.map((r, i) => (
                      <li key={i} className="border-l-2 border-sky-500 pl-2 text-rescue-mute italic">
                        « {r.reassignment_reason} »
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
